/**
 * Password Link Result
 *
 * Shows a freshly generated one-time password link — exactly once. The server
 * keeps only the token's hash, so once this is closed the link cannot be
 * shown again; the admin generates a new one instead.
 *
 * The URL is built here, on the admin's own origin: the admin app and the
 * /reset-password page are served from the same host, which avoids trusting a
 * Host header or depending on a public-base-URL setting.
 */

import React, { useCallback, useState } from 'react';
import { Button, Input, Label } from '@lognai/synapse-design-system-v2';
import { formatDate } from '@lognai/plugin-sdk';
import type { PasswordLink } from '@lognai/plugin-admin';
import { ConfirmDialog } from '../shared';

/** `/reset-password?token=…`, plus `&welcome=1` for a first-password link. */
export const buildPasswordLinkUrl = (link: Pick<PasswordLink, 'token' | 'purpose'>): string => {
  const welcome = link.purpose === 'SET_INITIAL' ? '&welcome=1' : '';
  return `${window.location.origin}/reset-password?token=${encodeURIComponent(link.token)}${welcome}`;
};

export interface PasswordLinkResultProps {
  link: PasswordLink;
  /** Who the link is for, shown in the sharing note. */
  userLabel: string;
  /** Called once the link is on the clipboard. */
  onCopied?: () => void;
}

export function PasswordLinkResult({ link, userLabel, onCopied }: PasswordLinkResultProps) {
  const url = buildPasswordLinkUrl(link);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
      onCopied?.();
    } catch {
      // Clipboard can be blocked (permissions, insecure context); the field
      // is selectable, so the admin can still copy it by hand.
      setCopyState('failed');
    }
  };

  const purposeText =
    link.purpose === 'SET_INITIAL'
      ? 'They open it to choose their first password.'
      : 'They open it to choose a new password. Their current password keeps working until they do.';

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="password-link-url">Password link</Label>
        <div className="flex gap-2">
          <Input
            id="password-link-url"
            readOnly
            value={url}
            className="font-mono text-xs"
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          />
          <Button type="button" variant="outline" onClick={copy}>
            {copyState === 'copied' ? 'Copied' : 'Copy'}
          </Button>
        </div>
        {copyState === 'failed' && (
          <p className="text-sm text-muted-foreground">
            Could not copy automatically — select the link and copy it.
          </p>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Expires {formatDate(link.expiresAt as Date | string, 'd MMM yyyy, HH:mm')}. Single use.{' '}
        {purposeText}
      </p>
      <p className="text-sm font-medium">
        Copy it now and share it with {userLabel} securely — this link won't be shown again.
      </p>
    </div>
  );
}

/**
 * Guards a dialog that shows a one-time link. Closing it (overlay, Esc, X or
 * Done) before the link was copied loses the link for good, and the 15-minute
 * cooldown then blocks generating another — so an uncopied close asks first.
 */
export function useLinkCloseGuard(hasLink: boolean, onOpenChange: (open: boolean) => void) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const requestOpenChange = (open: boolean) => {
    if (!open && hasLink && !copied) {
      setConfirming(true);
      return;
    }
    onOpenChange(open);
  };

  const reset = useCallback(() => {
    setCopied(false);
    setConfirming(false);
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      open={confirming}
      onOpenChange={setConfirming}
      title="Close without copying the link?"
      description="You'll need to wait 15 minutes to generate another."
      confirmButtonText="Close anyway"
      cancelButtonText="Keep it open"
      onConfirm={() => {
        setConfirming(false);
        onOpenChange(false);
      }}
    />
  );

  const markCopied = useCallback(() => setCopied(true), []);

  return { requestOpenChange, markCopied, reset, confirmDialog };
}
